import { FormField, FormLabel, FormSelect } from '@/components';
import { useTranslation } from 'react-i18next';
import type { CompanyFormData } from '../models';
import { getCountryIconUrl } from '../../country/country';
import type { Country } from '../../country/country';
import { FormSection } from '@/components/FormSection';

interface CompanyDataSectionProps {
    formData: CompanyFormData;
    countries: Country[];
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    showCustomerId?: boolean;
    isCustomerIdReadOnly?: boolean;
}

export function CompanyDataSection({
    formData,
    countries,
    onChange,
    showCustomerId = false,
    isCustomerIdReadOnly = false
}: CompanyDataSectionProps) {
    const { t } = useTranslation();
    const shouldShowCustomerId =
        showCustomerId &&
        (!isCustomerIdReadOnly ||
            (formData.customerId !== null && formData.customerId !== '' && formData.customerId !== undefined));

    return (
        <FormSection icon="company" title="Firmendaten">
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-3 w-full">
                <FormField
                    labelKey="companyAdmin.form.companyName"
                    label="Firmenname"
                    name="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={onChange}
                    className="sm:col-span-2"
                    required
                />

                {shouldShowCustomerId && (
                    <CustomerIdField
                        customerId={formData.customerId}
                        isReadOnly={isCustomerIdReadOnly}
                        onChange={onChange}
                    />
                )}

                <FormSelect
                    labelKey="companyAdmin.form.country"
                    label="Land"
                    name="country"
                    value={formData.country}
                    onChange={onChange}
                    className="sm:col-span-2"
                    required
                    data-hs-select={JSON.stringify({
                        placeholder: t('companyAdmin.form.countryPlaceholder'),
                        hasSearch: true,
                        searchPlaceholder: t('companyAdmin.form.countryPlaceholder'),
                        searchAttributes: {
                            autocomplete: "off"
                        },
                        searchClasses:
                            "block py-1.5 sm:py-2 px-3 w-full sm:text-sm border-border rounded-lg focus:border-primary focus:ring-primary bg-field text-onfield",
                        searchWrapperClasses:
                            "bg-surface p-2 -mx-1 sticky top-0",
                        toggleTag:
                            '<button type="button" aria-expanded="false"><span data-icon></span><span class="text-onsurface" data-title></span></button>',
                        toggleClasses:
                            "relative py-2 ps-3 pe-9 flex gap-x-2 w-full cursor-pointer bg-field border border-border rounded-lg text-sm text-onfield",
                        dropdownClasses:
                            "max-h-72 p-1 pt-0 z-50 w-full overflow-y-auto bg-surface border border-border rounded-xl shadow-xl",
                        optionClasses:
                            "py-2 px-4 w-full text-sm cursor-pointer hover:bg-surface-darker rounded-lg text-onsurface",
                        optionTemplate:
                            '<div class="flex items-center"><div class="me-2" data-icon></div><div data-title></div></div>',
                    })}
                >
                    <option value="">{t('companyAdmin.form.countryPlaceholder')}</option>
                    {countries.map((country) => (
                        <option
                            key={country.id}
                            value={country.id}
                            data-hs-select-option={
                                JSON.stringify({
                                    icon: `<img class="inline-block size-4 rounded-full" src="${getCountryIconUrl(country.code)}" alt="${country.name}" />`
                                })}
                        >
                            {country.name}
                        </option>
                    ))}
                </FormSelect>

                <FormField
                    labelKey="companyAdmin.form.taxNumber"
                    label="Steuernummer"
                    name="taxNumber"
                    type="text"
                    value={formData.taxNumber}
                    onChange={onChange}
                />

                <FormField
                    labelKey="companyAdmin.form.registrationNumber"
                    label="UID-Nummer"
                    name="uidNumber"
                    type="text"
                    value={formData.uidNumber}
                    onChange={onChange}
                    required
                />
            </div>
        </FormSection>
    );
}

interface CustomerIdFieldProps {
    customerId: string;
    isReadOnly: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

function CustomerIdField({ customerId, isReadOnly, onChange }: CustomerIdFieldProps) {
    if (isReadOnly) {
        return (
            <FormLabel
                labelKey="companyAdmin.form.customerId"
                label="Customer ID"
                value={customerId}
                className="sm:col-span-2"
            />
        );
    }

    return (
        <FormField
            labelKey="companyAdmin.form.customerId"
            label="Customer ID"
            name="customerId"
            type="text"
            value={customerId}
            onChange={onChange}
            className="sm:col-span-2"
        />
    );
}
